import React, { useState } from 'react'
import { Typography, Divider, IconButton, Tooltip } from '@mui/material'
import { format } from 'date-fns'
import {
    faEdit,
    faHistory,
    faCheck,
    faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import EntFeature from 'models/EntFeature'
import { useUiStateContext } from 'context/UiState/UiStateContext'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { MidHistory } from '../modals/MidHistory'
import idDirectory from '../idAttributes'

interface Detail {
    label: string
    value: React.ReactNode
}

const UserStatus = ({ status }: { status: boolean }) => {
    const { clientTheme } = useThemeStyling()
    return (
        <div
            className={'emp-statusContainer'}
            style={{ fontSize: clientTheme.typography.body1.fontSize }}
        >
            <FontAwesomeIcon
                icon={status ? faCheck : faTimes}
                style={{
                    color: status ? 'green' : 'red',
                    marginRight: '8px',
                    fontSize: '14px',
                }}
            />
            {status ? 'Active' : 'Inactive'}
        </div>
    )
}

export const MerchantMidsGridDetails = (props: any) => {
    const { clientTheme } = useThemeStyling()
    const { user } = useAuthedUser()
    const uiState = useUiStateContext()

    const isReadOnly = user?.is_read_only
    const isReadOnlyEditMid = !uiState.whoami?.hasRole(EntFeature.EDIT_MID)

    const [openMidHistoryModal, setOpenMidHistoryModal] = useState<boolean>(
        false
    )

    const {
        selectedRowData,
        // availableDeflectors,
        handleMidsListEditMid,
        showClientTypeIssuerUI,
        showEditAccountNumber,
    } = props

    const {
        // MID_DETAILS,
        // DEFLECTORS,
        // SHOW_HELIX_MID = false,
        SHOW_MIDS_WPG_FIELD = false,
        SHOW_MID_HISTORY,
    } = useFeatureToggle('MERCHANT_HIERARCHY')
    // const {
    //     enabled: helixMIDEnabled,
    //     input_edit_label: helixMIDlabel,
    // } = SHOW_HELIX_MID
    const { enabled: WPGFieldEnabled } = SHOW_MIDS_WPG_FIELD

    const detailsSection1: Detail[] = [
        // status
        {
            label: 'Status',
            value: (
                <UserStatus
                    status={selectedRowData?.status?.id === 2 || false}
                />
            ),
        },
    ]

    const detailsSection2: Detail[] = [
        // account number, account alias, client name, wpg, date onboarded
        {
            label: 'Account Number',
            value: selectedRowData?.mid ?? 'Unavailable',
        },
        {
            label: 'Account Alias',
            value: selectedRowData?.alias ?? 'Unavailable',
        },
        {
            label: 'Client Name',
            value: selectedRowData?.descriptor?.descriptor ?? 'Unavailable',
        },
        ...(WPGFieldEnabled
            ? [
                  {
                      label: 'WPG Merchant Code',
                      value:
                          selectedRowData?.wpg_merchant_code ?? 'Unavailable',
                  },
              ]
            : []),
        {
            label: 'Date Onboarded',
            value: selectedRowData?.date_created
                ? format(new Date(selectedRowData?.date_created), 'yyyy/MM/dd')
                : 'Unavailable',
        },
    ]

    const detailsSection3: Detail[] = [
        // scheme, internal reference id, group id, first name, last name, email, phone, platform name, platform url location, platform username, platform password
        {
            label: 'Scheme',
            value: selectedRowData?.scheme ?? 'Unavailable',
        },
        {
            label: 'Internal Reference ID',
            value: selectedRowData?.internal_reference_id ?? 'Unavailable',
        },
        {
            label: 'Group ID',
            value: selectedRowData?.group_id ?? 'Unavailable',
        },
        {
            label: 'First Name',
            value: selectedRowData?.first_name ?? 'Unavailable',
        },
        {
            label: 'Last Name',
            value: selectedRowData?.last_name ?? 'Unavailable',
        },
        {
            label: 'Email',
            value: selectedRowData?.email ?? 'Unavailable',
        },
        {
            label: 'Phone',
            value: selectedRowData?.phone ?? 'Unavailable',
        },
        {
            label: 'Platform Name',
            value: selectedRowData?.platform_name ?? 'Unavailable',
        },
        {
            label: 'Platform URL Location',
            value: selectedRowData?.platform_url_location ?? 'Unavailable',
        },
        {
            label: 'Platform Username',
            value: selectedRowData?.platform_username ?? 'Unavailable',
        },
    ]

    const detailsSection4: Detail[] =
        // DEFLECTORS?.enabled && availableDeflectors
        //     ? availableDeflectors.map(
        //           (availableDeflector: {
        //               alias: string
        //               id: number
        //               name: string
        //           }) => {
        //               let foundDeflector = undefined as any
        //               try {
        //                   foundDeflector =
        //                       selectedRowData?.deflectors &&
        //                       selectedRowData.deflectors?.find((obj: any) => {
        //                           return obj.id === availableDeflector.id
        //                       })
        //               } catch (e) {}

        //               return {
        //                   label: availableDeflector?.name,
        //                   value: foundDeflector
        //                       ? deflectorStatusIdToText(
        //                             foundDeflector?.status?.id
        //                         )
        //                       : 'Inactive',
        //               }
        //           }
        //       )
        //     :
        []

    const detailsAllSections: Detail[][] = [
        ...(detailsSection1.length > 0 ? [detailsSection1] : []),
        ...(detailsSection2.length > 0 ? [detailsSection2] : []),
        ...(detailsSection3.length > 0 ? [detailsSection3] : []),
        ...(detailsSection4.length > 0 ? [detailsSection4] : []),
    ]

    return (
        <div id={'merchantSettingsMerchantMidsGridDetails'}>
            {selectedRowData ? (
                <div
                    className={'emp-root'}
                    id={idDirectory.merchantMidsGridDetails.divRoot}
                >
                    <div className={'emp-headerContainer'}>
                        <Typography variant="h5" className={'emp-header'}>
                            {`${
                                showClientTypeIssuerUI
                                    ? 'Account Number'
                                    : 'MID'
                            } Details`}
                        </Typography>
                        <div className={'emp-iconContainer'}>
                            {!isReadOnlyEditMid && showEditAccountNumber && (
                                <Tooltip
                                    arrow
                                    placement="top"
                                    title={`Edit ${
                                        showClientTypeIssuerUI
                                            ? 'Account Number'
                                            : 'MID'
                                    }`}
                                >
                                    <div>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleMidsListEditMid(
                                                    selectedRowData
                                                )
                                            }
                                            disabled={isReadOnly}
                                            id={
                                                idDirectory
                                                    .merchantMidsGridDetails
                                                    .btnEdit
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faEdit}
                                                className={`${
                                                    isReadOnly &&
                                                    'emp-disabledIcon'
                                                }`}
                                                style={{
                                                    color:
                                                        clientTheme.secondary,
                                                }}
                                            />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            )}
                            {SHOW_MID_HISTORY.enabled && (
                                <Tooltip
                                    arrow
                                    placement="top"
                                    title={`${
                                        showClientTypeIssuerUI
                                            ? 'Account Number'
                                            : 'MID'
                                    } History`}
                                >
                                    <div>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setOpenMidHistoryModal(true)
                                            }
                                            id={
                                                idDirectory
                                                    .merchantMidsGridDetails
                                                    .btnMidHistory
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faHistory}
                                                style={{
                                                    color:
                                                        clientTheme.secondary,
                                                }}
                                            />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {detailsAllSections.map((section, sectionIndex) => (
                        <div key={`section-${sectionIndex}`}>
                            <Divider className={'emp-divider'} />
                            {section.map((detail) => (
                                <DetailLabel key={detail.label} {...detail} />
                            ))}
                        </div>
                    ))}

                    {openMidHistoryModal && (
                        <MidHistory
                            openMidHistory={openMidHistoryModal}
                            onClose={() => setOpenMidHistoryModal(false)}
                            midId={selectedRowData.id}
                            showClientTypeIssuerUI={showClientTypeIssuerUI}
                        />
                    )}
                </div>
            ) : (
                <div
                    className={'emp-noSelectedRowRoot'}
                    id={idDirectory.merchantMidsGridDetails.divNoSelectedRow}
                >
                    <Typography variant="h5">
                        {`You must select a row to see ${
                            showClientTypeIssuerUI ? 'Account Number' : 'MID'
                        } details.`}
                    </Typography>
                </div>
            )}
        </div>
    )
}

const DetailLabel = (detail: Detail) => (
    <div className={'emp-detailContainer'}>
        <Typography className={'emp-label'}>{detail.label}:</Typography>

        {typeof detail.value === 'string' ? (
            <Typography
                sx={{
                    // wrap text
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                }}
            >
                {detail.value}
            </Typography>
        ) : (
            detail.value
        )}
    </div>
)
